import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Select, { createFilter } from 'react-select';
import {
  Button,
  Form,
  FormFeedback,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { fetchTaxonomies } from '../../../actions/Metadata';

import { APImoveTerm } from '../../../api/Metadata';

import Console from '../../../helpers/Console';
import { addFlashMessage } from '../../../helpers/FlashMessages';
import { searchDuplicateTerms } from '../../../helpers/Terms';

import ErrorBoundary from '../../ErrorBoundary';
import ProcessingSpinner from '../../ProcessingSpinner';

const TermMoveModal = ({
  dispatch,
  taxonomyId,
  selectedTermsIds,
  taxonomies,
  handleClose,
  afterMoveTerm,
}) => {
  const intl = useIntl();

  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState(null);
  const [filteredTaxonomies, setFilteredTaxonomies] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchTaxonomies());
  }, [dispatch]);

  useEffect(() => {
    if (taxonomies) {
      const filtered = taxonomies.filter(taxonomy => taxonomy.id.toString() !== taxonomyId);
      setFilteredTaxonomies(filtered);
    }
  }, [taxonomies, taxonomyId]);

  useEffect(() => {
    if (selectedTaxonomyId) {
      setDuplicates(
        searchDuplicateTerms(taxonomyId, selectedTaxonomyId, taxonomies, selectedTermsIds),
      );
    }
  }, [selectedTaxonomyId, selectedTermsIds, taxonomies, taxonomyId]);

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      const response = await APImoveTerm(taxonomyId, selectedTaxonomyId, selectedTermsIds);
      if (response.status !== 200) {
        throw new Error(intl.formatMessage({ defaultMessage: 'Network response was not ok' }));
      }
      afterMoveTerm(response.data);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Tags have been moved' }),
      });
      handleClose();
    } catch (e) {
      Console.error(e);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getTaxonomiesOptions = () => {
    const options = [];
    filteredTaxonomies.forEach((taxonomy) => {
      options.push({
        value: taxonomy.id,
        label: taxonomy.plural,
      });
    });

    return options;
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Move tags' })}
      </ModalHeader>
      <ModalBody>
        <Form className="move-taxonomy-form" onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
          <FormGroup>
            <Label for="taxonomy">
              {intl.formatMessage({ defaultMessage: 'Select the category you want to move the tags to' })}
            </Label>
            <Select
              id="taxonomy"
              className="file-status-select"
              classNamePrefix="file-status-select"
              name="taxonomy"
              filterOption={createFilter({
                ignoreAccents: false,
              })}
              placeholder={intl.formatMessage({ defaultMessage: 'Select' })}
              options={getTaxonomiesOptions()}
              onChange={(v) => { setSelectedTaxonomyId(v.value); }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
            {error && (
              <FormFeedback style={{ display: 'block' }}>
                {intl.formatMessage({ defaultMessage: 'An error occured. Please try again later.' })}
              </FormFeedback>
            )}
            {duplicates.length > 0 && (
              <div className="mt-4">
                <strong>
                  {intl.formatMessage({ defaultMessage: 'The target category already contains the following tags:' })}
                </strong>
                <div className="tags-list">
                  {duplicates.map(duplicate => (
                    <span key={duplicate.id} className="tag tag-label">
                      {duplicate.name}
                    </span>
                  ))}
                </div>
                {intl.formatMessage({ defaultMessage: 'These tags will be merged.' })}
              </div>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={isLoading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={isLoading || !selectedTaxonomyId}
            onClick={handleUpdate}
          >
            <ProcessingSpinner show={isLoading} />
            {intl.formatMessage({ defaultMessage: 'Move' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state) => ({
  taxonomies: state.Metadata.taxonomies,
});

TermMoveModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  taxonomyId: PropTypes.string,
  selectedTermsIds: PropTypes.array.isRequired,
  taxonomies: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  afterMoveTerm: PropTypes.func,
};

TermMoveModal.defaultProps = {
  taxonomyId: null,
  afterMoveTerm: () => {},
};

export default connect(mapStateToProps)(TermMoveModal);
